export interface MetadataEntryLike {
  id?: string | null;
  key?: string | null;
  value?: unknown;
  order?: number | null;
  type?: string | null;
  editable?: boolean | null;
}

export type MetadataChange =
  | { op: 'add'; key: string; value: unknown; type?: string }
  | { op: 'set'; key: string; value: unknown }
  | { op: 'rename'; from: string; to: string }
  | { op: 'remove'; key: string };

export interface MetadataFanOut {
  sourceId: string;
  source: MetadataEntryLike[];
  changes: MetadataChange[];
}

const isSystem = (entry: MetadataEntryLike) => entry?.type === 'system';

const isBlank = (entry: MetadataEntryLike) =>
  !String(entry?.key ?? '').trim() && !String(entry?.value ?? '').trim();

const visible = (entries: MetadataEntryLike[] | null | undefined) =>
  (entries ?? []).filter((entry) => entry && !isSystem(entry) && !isBlank(entry));

const keyOf = (entry: MetadataEntryLike) => String(entry.key ?? '');

const sameValue = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');

export function metadataChangesBetween(
  before: MetadataEntryLike[] | null | undefined,
  after: MetadataEntryLike[] | null | undefined,
): MetadataChange[] {
  const oldRows = visible(before);
  const newRows = visible(after);
  const pairs: Array<[MetadataEntryLike, MetadataEntryLike]> = [];
  const oldLeft = new Set(oldRows);
  const newLeft = new Set(newRows);

  for (const next of newRows) {
    if (!next.id) continue;
    const prev = [...oldLeft].find((row) => row.id === next.id);
    if (prev) {
      pairs.push([prev, next]);
      oldLeft.delete(prev);
      newLeft.delete(next);
    }
  }
  for (const next of [...newLeft]) {
    const prev = [...oldLeft].find((row) => keyOf(row) === keyOf(next));
    if (prev) {
      pairs.push([prev, next]);
      oldLeft.delete(prev);
      newLeft.delete(next);
    }
  }
  if (oldLeft.size === newLeft.size) {
    const olds = [...oldLeft];
    const news = [...newLeft];
    olds.forEach((prev, index) => pairs.push([prev, news[index]]));
    oldLeft.clear();
    newLeft.clear();
  }

  const removes: MetadataChange[] = [...oldLeft].map((row) => ({ op: 'remove', key: keyOf(row) }));
  const renames: MetadataChange[] = [];
  const sets: MetadataChange[] = [];
  for (const [prev, next] of pairs) {
    if (keyOf(prev) !== keyOf(next)) {
      renames.push({ op: 'rename', from: keyOf(prev), to: keyOf(next) });
    }
    if (!sameValue(prev.value, next.value)) {
      sets.push({ op: 'set', key: keyOf(next), value: next.value ?? '' });
    }
  }
  const adds: MetadataChange[] = [...newLeft].map((row) => ({
    op: 'add',
    key: keyOf(row),
    value: row.value ?? '',
    ...(row.type ? { type: row.type } : {}),
  }));
  return [...removes, ...renames, ...sets, ...adds];
}

export function applyMetadataChanges<T extends MetadataEntryLike>(
  entries: T[] | null | undefined,
  changes: MetadataChange[],
): T[] {
  let rows: T[] = (entries ?? []).map((entry) => ({ ...entry }));
  const nextOrder = () =>
    rows.reduce((max, row) => Math.max(max, Number(row.order ?? -1)), -1) + 1;
  const matching = (key: string) => rows.filter((row) => !isSystem(row) && keyOf(row) === key);

  for (const change of changes) {
    switch (change.op) {
      case 'remove':
        rows = rows.filter((row) => isSystem(row) || keyOf(row) !== change.key);
        break;
      case 'rename':
        matching(change.from).forEach((row) => (row.key = change.to));
        break;
      case 'set':
      case 'add': {
        const found = matching(change.key);
        if (found.length) {
          found.forEach((row) => (row.value = change.value));
        } else {
          rows.push({
            key: change.key,
            value: change.value,
            type: change.op === 'add' && change.type ? change.type : 'string',
            order: nextOrder(),
            editable: true,
          } as T);
        }
        break;
      }
    }
  }
  return rows;
}

/** Ids survive only where the element already owns them, so no row is ever shared. */
export function metadataAfterFanOut<T extends MetadataEntryLike>(
  fanOut: MetadataFanOut,
  elementId: string,
  current: T[] | null | undefined,
): T[] {
  const owned = new Set((current ?? []).map((row) => row?.id).filter(Boolean));
  if (elementId === fanOut.sourceId) {
    return (fanOut.source ?? [])
      .filter((row) => row && !isBlank(row))
      .map((row) => {
        const copy = { ...row } as T;
        if (!copy.id || !owned.has(copy.id)) delete copy.id;
        return copy;
      });
  }
  return applyMetadataChanges(current, fanOut.changes);
}

export function sameVisibleMetadata(
  a: MetadataEntryLike[] | null | undefined,
  b: MetadataEntryLike[] | null | undefined,
): boolean {
  const byOrder = (rows: MetadataEntryLike[]) =>
    rows.map((row, index) => ({ row, index }))
      .sort((x, y) => Number(x.row.order ?? x.index) - Number(y.row.order ?? y.index) || x.index - y.index)
      .map(({ row }) => row);
  const left = byOrder(visible(a));
  const right = byOrder(visible(b));
  return (
    left.length === right.length &&
    left.every(
      (row, index) => keyOf(row) === keyOf(right[index]) && sameValue(row.value, right[index].value),
    )
  );
}
